import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, CheckCircle2, Circle, GraduationCap, Pencil, PlayCircle, Plus, Search } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { startTour } from "../components/Walkthrough";
import { TOURS } from "../walkthrough/tours";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, PageHeader, TableSkeleton } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

function GuideEditor({ open, onClose, article, onSaved }) {
  const [form, setForm] = useState({ slug: "", title: "", category: "General", summary: "", sections: [], faqs: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm(article ? { slug: article.slug, title: article.title, category: article.category,
      summary: article.summary, sections: article.sections || [], faqs: article.faqs || [] }
      : { slug: "", title: "", category: "General", summary: "", sections: [{ heading: "", body: "" }], faqs: [] });
  }, [article, open]);

  const save = async () => {
    if (!form.title.trim() || !form.slug.trim()) { toast.error("Title and slug are required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, module: form.slug, published: true,
        sections: form.sections.filter((s) => s.heading.trim()), faqs: form.faqs.filter((f) => f.q?.trim()) };
      if (article) await api.put(`/help/articles/${article.id}`, payload);
      else await api.post("/help/articles", payload);
      toast.success("Guide saved");
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900" data-testid="guide-editor">
        <DialogHeader><DialogTitle className="font-display">{article ? "Edit Guide" : "New Guide"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Title *</Label><Input data-testid="guide-title" className="mt-1.5" value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value, slug: article ? f.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") }))} /></div>
          <div><Label>Slug *</Label><Input data-testid="guide-slug" className="mt-1.5 font-mono" value={form.slug} disabled={!!article}
            onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} /></div>
          <div><Label>Category</Label><Input className="mt-1.5" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} /></div>
          <div className="col-span-2"><Label>Summary</Label><Input className="mt-1.5" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} /></div>
        </div>
        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="font-semibold">Sections</Label>
            <Button size="sm" variant="outline" data-testid="add-section-btn"
              onClick={() => setForm((f) => ({ ...f, sections: [...f.sections, { heading: "", body: "" }] }))}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Section
            </Button>
          </div>
          {form.sections.map((s, i) => (
            <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <Input placeholder="Section heading" value={s.heading}
                onChange={(e) => setForm((f) => ({ ...f, sections: f.sections.map((x, j) => j === i ? { ...x, heading: e.target.value } : x) }))} />
              <Textarea rows={3} placeholder="Explanation in simple words..." value={s.body}
                onChange={(e) => setForm((f) => ({ ...f, sections: f.sections.map((x, j) => j === i ? { ...x, body: e.target.value } : x) }))} />
            </div>
          ))}
          <div className="flex items-center justify-between mt-4">
            <Label className="font-semibold">FAQs</Label>
            <Button size="sm" variant="outline" data-testid="add-faq-btn"
              onClick={() => setForm((f) => ({ ...f, faqs: [...f.faqs, { q: "", a: "" }] }))}>
              <Plus className="mr-1 h-3.5 w-3.5" /> FAQ
            </Button>
          </div>
          {form.faqs.map((fq, i) => (
            <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-2">
              <Input placeholder="Question" value={fq.q}
                onChange={(e) => setForm((f) => ({ ...f, faqs: f.faqs.map((x, j) => j === i ? { ...x, q: e.target.value } : x) }))} />
              <Textarea rows={2} placeholder="Answer" value={fq.a}
                onChange={(e) => setForm((f) => ({ ...f, faqs: f.faqs.map((x, j) => j === i ? { ...x, a: e.target.value } : x) }))} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="guide-save-btn" onClick={save} disabled={busy} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Save Guide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function HelpCenter() {
  const { user } = useAuth();
  const { activeId, role } = useCompany();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [article, setArticle] = useState(null);
  const [editor, setEditor] = useState({ open: false, article: null });
  const [doneTours, setDoneTours] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kg_tours_completed") || "[]"); } catch { return []; }
  });
  const canManage = user?.is_super_admin || ["admin"].includes(role);

  useEffect(() => {
    const onDone = () => {
      try { setDoneTours(JSON.parse(localStorage.getItem("kg_tours_completed") || "[]")); } catch { /* noop */ }
    };
    window.addEventListener("kg:tour-finished", onDone);
    return () => window.removeEventListener("kg:tour-finished", onDone);
  }, []);

  const load = () => {
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (category !== "all") qs.set("category", category);
    api.get(`/help/articles?${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [activeId, category]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const openArticle = async (slug) => {
    try { const { data: a } = await api.get(`/help/articles/${slug}`); setArticle(a); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const markComplete = async () => {
    await api.post(`/help/articles/${article.slug}/complete`).catch(() => {});
    toast.success("Marked as completed");
    setArticle({ ...article });
    load();
  };

  if (article) {
    return (
      <div data-testid="help-article-page" className="max-w-3xl">
        <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => { setArticle(null); load(); }} data-testid="back-to-help">
          <ArrowLeft className="mr-1 h-4 w-4" /> Help Center
        </Button>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Badge variant="secondary" className="mb-2">{article.category}</Badge>
              <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="article-title">{article.title}</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{article.summary}</p>
            </div>
            {canManage && (
              <Button size="sm" variant="outline" data-testid="edit-guide-btn" onClick={() => setEditor({ open: true, article })}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </div>
          <div className="mt-6 space-y-6">
            {(article.sections || []).map((s, i) => (
              <div key={i}>
                <h3 className="font-display font-semibold flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-[10px] font-bold">{i + 1}</span>
                  {s.heading}
                </h3>
                <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed pl-7">{s.body}</p>
              </div>
            ))}
          </div>
          {article.faqs?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-display font-semibold mb-2">FAQs</h3>
              <Accordion type="single" collapsible>
                {article.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger data-testid={`faq-${i}`} className="text-sm text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-600 dark:text-slate-300">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
          <div className="mt-8 flex gap-2 border-t border-slate-100 dark:border-slate-800 pt-5">
            <Button data-testid="mark-complete-btn" onClick={markComplete}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="mr-1.5 h-4 w-4" /> Mark as completed
            </Button>
          </div>
        </div>
        <GuideEditor open={editor.open} article={editor.article}
          onClose={() => setEditor({ open: false, article: null })} onSaved={() => openArticle(article.slug)} />
      </div>
    );
  }

  return (
    <div data-testid="help-center-page">
      <PageHeader title="Help & Learning Center" testId="help-title"
        sub="Guides, workflows and FAQs — learn every feature inside the app">
        {canManage && (
          <Button data-testid="new-guide-btn" onClick={() => setEditor({ open: true, article: null })}
            className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Guide</Button>
        )}
      </PageHeader>

      {data && data.total > 0 && (
        <div className="mb-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex items-center gap-4">
          <GraduationCap className="h-8 w-8 text-amber-500 shrink-0" />
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-1.5">
              <span className="font-medium">Your learning progress</span>
              <span className="font-mono text-xs text-slate-500" data-testid="learning-progress">{data.completed}/{data.total} guides</span>
            </div>
            <Progress value={data.total ? (data.completed / data.total) * 100 : 0} className="h-2" />
          </div>
        </div>
      )}

      <div className="mb-6" data-testid="walkthroughs-section">
        <p className="font-display font-semibold mb-2.5 flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-amber-500" /> Interactive Walkthroughs
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOURS.map((t) => {
            const done = doneTours.includes(t.id);
            return (
              <div key={t.id}
                className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-display font-semibold text-sm flex items-center gap-1.5">
                    {t.title}
                    {done && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{t.description}</p>
                </div>
                <Button size="sm" variant={done ? "outline" : "default"} data-testid={`tour-${t.id}-btn`}
                  onClick={() => startTour(t.id)}
                  className={done ? "" : "bg-slate-900 dark:bg-slate-100 dark:text-slate-900"}>
                  {done ? "Replay" : "Start"}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="help-search" className="pl-9 bg-white dark:bg-slate-900"
            placeholder="How to create invoice? What is advance?..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["all", ...(data?.categories || [])].map((c) => (
            <Button key={c} size="sm" variant={category === c ? "default" : "outline"}
              data-testid={`help-cat-${c.toLowerCase()}`} onClick={() => setCategory(c)}
              className={category === c ? "bg-slate-900 dark:bg-slate-100 dark:text-slate-900" : ""}>
              {c === "all" ? "All" : c}
            </Button>
          ))}
        </div>
      </div>

      {!data ? <TableSkeleton /> : data.items.length === 0 ? (
        <EmptyState testId="help-empty" title="No guides found" body="Try a different search, or check back after an admin publishes more content." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="help-grid">
          {data.items.map((a) => (
            <button key={a.slug} data-testid={`guide-${a.slug}`} onClick={() => openArticle(a.slug)}
              className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 text-left hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all">
              <div className="flex items-start justify-between">
                <BookOpen className="h-5 w-5 text-slate-400 group-hover:text-amber-500 transition-colors" />
                {a.completed
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  : <Circle className="h-4 w-4 text-slate-200 dark:text-slate-700" />}
              </div>
              <p className="mt-3 font-display font-semibold leading-snug">{a.title}</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{a.summary}</p>
              <Badge variant="secondary" className="mt-3 text-[10px]">{a.category}</Badge>
            </button>
          ))}
        </div>
      )}

      <GuideEditor open={editor.open} article={editor.article}
        onClose={() => setEditor({ open: false, article: null })} onSaved={load} />
    </div>
  );
}
